import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Table, Button, message, Input as AntdInput } from 'antd'
import { createUseStyles } from 'react-jss'
import { PageHeader } from '../Common'
import { useAsync } from '../../hooks'
import {
  usePracticeOfficeLocation,
  addPracticeOfficeLocation,
  usePracticeOfficeLocationPagination,
  deletePracticeOfficeLocation,
  updatePracticeOfficeLocation,
  useFetchPracticeOfficeLocation,
} from '../../redux/slices/practice-locations'
import useQueryParams from '../../hooks/useQueryParams'
import api from '../../api'
import { useLocationsColumns } from './useColumns'

const useStyles = createUseStyles({
  container: {
    width: '70%',
    margin: '0 auto',
    marginBottom: '1rem',
  },
  center: {
    textAlign: 'center',
  },
  newPhraseContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: 'solid 1px #e8e8e8',
  },
  newLocation: {
    width: '100%',
    margin: '1rem',
  },
  rows: {
    '& td:last-child': {
      width: '5%',
      textAlign: 'center',
    },
  },
})

const QUERY_PARAMS_SCHEMA = { page: Number }

export default function PracticeLocations() {
  const { practiceId } = useParams()
  const classes = useStyles()
  const navigate = useNavigate()

  const [newLocation, setNewLocation] = useState('')
  const [editingLocationId, setEditingLocationId] = useState(null)
  const [editedLocationName, setEditedLocationName] = useState('')
  const [loading, setLoading] = useState('none')

  const getPractice = useCallback(
    () =>
      api.practices.getPractice(practiceId).then(practice => ({...practice, logo: practice.logoPath})),
    [practiceId],
  )

  const { value: practice } = useAsync(getPractice)

  const [conditions, setConditions] = useQueryParams({
    initialValues: { page: 1 },
    schema: QUERY_PARAMS_SCHEMA,
  })

  const practiceOfficeLocation = usePracticeOfficeLocation(practiceId)
  const pagination = usePracticeOfficeLocationPagination()

  const fetchPracticeOfficeLocation = useFetchPracticeOfficeLocation(
    practiceId,
    conditions.page,
    10,
  )

  useEffect(() => {
    setLoading('fetch')
    fetchPracticeOfficeLocation()
    setLoading('none')
  }, [conditions.page, fetchPracticeOfficeLocation])

  const handlePageChange = useCallback(
    page => {
      if (page !== conditions.page) {
        setConditions({ ...conditions, page })
      }
    },
    [conditions, setConditions],
  )

  const handleAddLocation = async () => {
    if (!newLocation.trim()) return
    setLoading('create')
    try {
      await addPracticeOfficeLocation(practiceId, { name: newLocation })
      await fetchPracticeOfficeLocation()
      message.success('Location added successfully!')
    } catch (error) {
      message.error('Failed to add location')
    } finally {
      setLoading('none')
      setNewLocation('')
    }
  }

  const handleDeleteLocation = async locationId => {
    setLoading('delete')
    try {
      await deletePracticeOfficeLocation(practiceId, locationId)
      await fetchPracticeOfficeLocation()
      message.success('Location deleted successfully!')
    } catch (error) {
      message.error('Failed to delete location')
    } finally {
      setLoading('none')
    }
  }

  const handleEditLocation = (locationId, currentName) => {
    setEditingLocationId(locationId)
    setEditedLocationName(currentName)
  }

  const handleConfirmEdit = async locationId => {
    setLoading('update')
    try {
      await updatePracticeOfficeLocation(practiceId, locationId, {
        name: editedLocationName,
      })
      await fetchPracticeOfficeLocation()
      message.success('Location updated successfully!')
      setEditingLocationId(null)
      setEditedLocationName('')
    } catch (error) {
      message.error('Failed to update location')
    } finally {
      setLoading('none')
    }
  }

  const columns = useLocationsColumns(
    editingLocationId,
    editedLocationName,
    setEditedLocationName,
    handleEditLocation,
    handleConfirmEdit,
    handleDeleteLocation,
    loading,
  )

  return (
    <div>
      <PageHeader
        title={`Office Locations - Practice ${practice?.name}`}
        onBack={() => navigate(-1)}
      />
      <div className={classes.container}>
        <div className={classes.newPhraseContainer}>
          <AntdInput
            className={classes.newLocation}
            placeholder="Location"
            value={newLocation}
            onChange={e => setNewLocation(e.target.value)}
          />
          <Button
            type="primary"
            disabled={!newLocation.trim()}
            onClick={handleAddLocation}
            loading={loading === 'create'}
          >
            Add New Location
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={practiceOfficeLocation}
          loading={loading === 'fetch'}
          rowClassName={classes.rows}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize || 10,
            onChange: handlePageChange,
            total: pagination.total || 0,
          }}
          rowKey={record => `${record.id}-${record.value}-${record.key}`}
        />
        <div className={classes.center}>
          <Button type="primary" onClick={() => navigate(-1)}>
            Done
          </Button>
        </div>
      </div>
    </div>
  )
}
